import {
  CSRF_SAFE_HEADER,
  CSRF_SAFE_HEADER_VALUE,
  TRACE_ID_HEADER,
  createTraceId,
  retry,
} from "@hex/common";

import { setUserBearerToken } from "./bearerToken";
import { authSwapToken } from "./data";

/** Marks a response where the swap token is spent or malformed, so retrying cannot help. */
class UnredeemableSwapTokenError extends Error {}

let exchange: Promise<void> | undefined;

/**
 * Trades the swap token the server rendered into this document for the page's
 * bearer token, and holds the bearer in memory.
 *
 * Returns `undefined` when the document has no swap token, which is the common
 * case — an ordinary signed-in page load authenticates with its session cookie
 * — so those loads pay no startup cost.
 *
 * Callers must let the returned promise settle before issuing an authenticated
 * request, because `createRequestHeaders` reads the bearer out of memory on
 * every call and there is nowhere else for it to come from.
 */
export function startAuthSwapTokenExchange(): Promise<void> | undefined {
  if (authSwapToken == null) {
    return undefined;
  }

  // If the swap token fails, we'll show the unauthenticate page
  exchange ??= redeemSwapToken(authSwapToken).catch((error: unknown) => {
    console.error("Failed to exchange auth swap token", error);
  });

  return exchange;
}

async function redeemSwapToken(swapToken: string): Promise<void> {
  const bearerToken = await retry({
    func: async () => {
      const response = await fetch("/auth/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          [CSRF_SAFE_HEADER]: CSRF_SAFE_HEADER_VALUE,
          [TRACE_ID_HEADER]: createTraceId(),
        },
        body: JSON.stringify({ swapToken }),
      });

      if (response.status >= 400 && response.status < 500) {
        throw new UnredeemableSwapTokenError(
          `Auth swap token rejected with status ${response.status}`,
        );
      }
      if (!response.ok) {
        throw new Error(
          `Auth swap token exchange failed with status ${response.status}`,
        );
      }

      const body = (await response.json()) as { bearerToken: string };
      return body.bearerToken;
    },
    retryCount: 2,
    interval: 150,
    shouldRetry: (error) => !(error instanceof UnredeemableSwapTokenError),
  });

  setUserBearerToken(bearerToken);
}
